import { useMemo, useState } from 'react';
import { Download, FileSpreadsheet, Loader2, User, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DateRangePicker } from '../common/DateRangePicker';
import { useAPIStorage } from '../../hooks/useAPIStorage';
import {
  exportDailyRangeCSV,
  exportTeamDailyXLSX,
  parseISODate,
  type TeamExportMember,
} from '../../utils/managerExport';
import type { DayEntry, TeamMemberEntry } from '../../types';

interface Props {
  teamMembers: TeamMemberEntry[];
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function monthStartISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

/** Liefert die Jahre, die ein [from, to]-Bereich beruehrt. */
function yearsInRange(from: Date, to: Date): number[] {
  const years: number[] = [];
  for (let y = from.getFullYear(); y <= to.getFullYear(); y += 1) {
    years.push(y);
  }
  return years;
}

function displayName(m: TeamMemberEntry): string {
  if (m.firstName && m.lastName) return `${m.firstName} ${m.lastName}`;
  return m.name;
}

/**
 * Manager-Export-Panel: Stundenliste fuer Team-Mitglieder.
 * Bewusst KEIN Zugriff auf getUserSettings / getPayrollSegments — keine Lohndaten.
 */
export function ManagerExportPanel({ teamMembers }: Props) {
  const { getUserEntries } = useAPIStorage();
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [from, setFrom] = useState<string>(monthStartISO);
  const [to, setTo] = useState<string>(todayISO);
  const [loading, setLoading] = useState<'single' | 'team' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sortedMembers = useMemo(
    () => [...teamMembers].sort((a, b) => displayName(a).localeCompare(displayName(b), 'de')),
    [teamMembers],
  );

  const selectedMember = teamMembers.find((m) => m.id === selectedUserId);
  const isExporting = loading !== null;
  const rangeValid = from.length === 10 && to.length === 10 && from <= to;

  async function loadEntriesForRange(userId: string, fromDate: Date, toDate: Date): Promise<Record<string, DayEntry>> {
    const years = yearsInRange(fromDate, toDate);
    const yearMaps = await Promise.all(years.map((y) => getUserEntries(userId, y)));
    const merged: Record<string, DayEntry> = {};
    for (const map of yearMaps) {
      if (!map) continue;
      Object.assign(merged, map);
    }
    return merged;
  }

  async function handleSingleExport() {
    if (!selectedMember) {
      setError('Bitte zuerst einen Mitarbeiter auswählen.');
      return;
    }
    setLoading('single');
    setError(null);
    try {
      const fromDate = parseISODate(from);
      const toDate = parseISODate(to);
      const entries = await loadEntriesForRange(selectedMember.id, fromDate, toDate);
      exportDailyRangeCSV(entries, fromDate, toDate, displayName(selectedMember));
    } catch (e) {
      console.error('Manager-Export (Einzel) fehlgeschlagen:', e);
      setError(
        e instanceof Error && /403|forbidden|nicht.*zugeordnet/i.test(e.message)
          ? 'Keine Berechtigung für diesen Mitarbeiter.'
          : 'Fehler beim Export. Bitte erneut versuchen.',
      );
    } finally {
      setLoading(null);
    }
  }

  async function handleTeamExport() {
    if (teamMembers.length === 0) {
      setError('Kein Team-Mitglied vorhanden.');
      return;
    }
    setLoading('team');
    setError(null);
    try {
      const fromDate = parseISODate(from);
      const toDate = parseISODate(to);

      const memberData: TeamExportMember[] = await Promise.all(
        sortedMembers.map(async (m) => ({
          userId: m.id,
          userName: displayName(m),
          entries: await loadEntriesForRange(m.id, fromDate, toDate),
        })),
      );

      await exportTeamDailyXLSX(memberData, fromDate, toDate);
    } catch (e) {
      console.error('Manager-Export (Team) fehlgeschlagen:', e);
      setError(
        e instanceof Error && /403|forbidden|nicht.*zugeordnet/i.test(e.message)
          ? 'Für mindestens einen Mitarbeiter fehlt die Berechtigung. Bitte Team-Zuordnung prüfen.'
          : 'Fehler beim Team-Export. Bitte erneut versuchen.',
      );
    } finally {
      setLoading(null);
    }
  }

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="rounded-md bg-blue-50 dark:bg-blue-900/30 p-1.5">
            <Download className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-50">Stundenliste exportieren</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Tagesdetails Ihres Teams im gewählten Zeitraum (keine Lohndaten)
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <div>
            <Label htmlFor="manager-export-employee" className="text-xs text-gray-600 dark:text-gray-400 mb-1">
              Mitarbeiter
            </Label>
            <Select
              value={selectedUserId}
              onValueChange={(v) => setSelectedUserId(v)}
              disabled={isExporting}
            >
              <SelectTrigger id="manager-export-employee">
                <div className="flex items-center gap-2">
                  {selectedUserId === 'all' ? (
                    <Users className="w-3.5 h-3.5 text-gray-400" />
                  ) : (
                    <User className="w-3.5 h-3.5 text-gray-400" />
                  )}
                  <SelectValue placeholder="Alle Team-Mitglieder" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Team-Mitglieder</SelectItem>
                {sortedMembers.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {displayName(m)}
                    {m.depth > 1 ? ' (indirekt)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-gray-600 dark:text-gray-400 mb-1">Zeitraum</Label>
            <DateRangePicker
              from={from}
              to={to}
              onChange={(f, t) => {
                setFrom(f);
                setTo(t);
              }}
            />
          </div>
        </div>

        {error && (
          <div className="mb-3 p-2 text-xs border border-red-300/60 bg-red-50 dark:border-red-500/20 dark:bg-red-900/10 rounded-lg text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {selectedUserId === 'all' ? (
            <Button
              onClick={handleTeamExport}
              disabled={isExporting || !rangeValid || teamMembers.length === 0}
              variant="outline"
              className="justify-start gap-3 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40"
            >
              {loading === 'team' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4" />
              )}
              Team-Stundenliste (XLSX, 1 Sheet pro Mitarbeiter)
            </Button>
          ) : (
            <Button
              onClick={handleSingleExport}
              disabled={isExporting || !rangeValid || !selectedMember}
              variant="secondary"
              size="sm"
            >
              {loading === 'single' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4" />
              )}
              Tagesdetails (CSV)
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
